import { FC, memo, useEffect, useMemo } from 'react';
import { useReactFlow } from 'reactflow';
import { getCollapsedState } from '@components/MainStage/collapsedLayers.store';
import CanvasEntityEditSaveHint from '@components/EntityDrawers/components/CanvasEntityEditSaveHint';
import DrawersOutlet from '@components/EntityDrawers/DrawersOutlet';
import { MainStage } from '@components/MainStage';
import useRedrawPhantomsOnFilters from '@components/MainStage/hooks/useRedrawPhantomsOnFilters';
import useStore from '@components/MainStage/store';
import { SIDE_MENU_WIDTH } from '@components/SideMenu/SideMenu';
import { INITIAL_VIEWPORT, NodesTypes } from '@constants/canvas/general';
import { LayerTypes, Modes } from '@constants/canvas/layers';
import { useProject } from '@context/Project/ProjectProvider';
import useCanvasZoom from '@hooks/canvas/useCanvasZoom';
import useResetViewport from '@hooks/canvas/useResetViewport';
import useAssets from '@hooks/useAssets';
import useLoading from '@hooks/useLoading';
import useProjectId from '@hooks/useProjectId';
import useScenarioMode from '@hooks/useScenarioMode';
import useToggle from '@hooks/useToggle';
import { Box, Stack } from '@mui/material';
import CanvasModelRenderer from '@utils/canvas/CanvasModelRenderer';
import { collapseRelationController } from '@utils/canvas/CollapseRelationController';
import { initCollapsedNodes } from '@utils/canvasHelpers';
import { formatEdgesForCanvas, formatNodesForCanvas } from '@utils/helpers';
import { Stage } from '@views/Insights/Stage';
import ControlsBar from '@views/Project/components/ControlsBar';
import { ListMenu } from '@views/Project/components/ListMenu';
import { Versions } from '@views/Project/components/Versions';
import useAddAsset from '@views/Project/hooks/useAddAsset';
import useCurrentAssetInitialize from '@views/Project/hooks/useCurrentAssetInitialize';
import useFetchNodes from '@views/Project/hooks/useFetchNodes';

export const Project: FC = memo(() => {
  const { beginLoading } = useLoading();
  const [isListMenuOpen, { toggle: toggleListMenu }] = useToggle(true);
  const id = useProjectId(true);
  const {
    activeVersionId,
    setActiveVersionId,
    isVersionsShown,
    setIsVersionsShown,
    toolbox,
    isInsightsMode,
  } = useProject();
  const { scenarioMode } = useScenarioMode();

  const { mode } = toolbox;
  const { setViewport } = useReactFlow();
  const { zoom } = useCanvasZoom();

  const { setNodes, setEdges } = useStore();
  const { isNewAssetMode } = useAssets();
  const addAsset = useAddAsset();
  const resetViewPort = useResetViewport();

  useCurrentAssetInitialize();
  useRedrawPhantomsOnFilters();

  const { data } = useFetchNodes();

  const canvasNodes = useMemo(() => {
    return mode ? formatNodesForCanvas(data?.nodes, mode) : [];
  }, [activeVersionId, data, mode]);

  const canvasEdges = useMemo(() => {
    return formatEdgesForCanvas(data?.relations);
  }, [activeVersionId, data]);

  useEffect(() => {
    setIsVersionsShown(false);
    setActiveVersionId('');
  }, [id]);

  useEffect(() => {
    setViewport(INITIAL_VIEWPORT(zoom));
  }, [id, mode, scenarioMode, isVersionsShown]);

  // Conceptual layer collapsed by default
  useEffect(() => {
    if (mode !== Modes.Controls || isInsightsMode) return;

    const { isCollapsed, collapse } = getCollapsedState();
    const shouldCollapse = !isCollapsed(LayerTypes.Conceptual);

    if (shouldCollapse) {
      collapse(LayerTypes.Conceptual);
    }

    return () => {
      if (shouldCollapse) {
        getCollapsedState().expand(LayerTypes.Conceptual);
      }
    };
  }, [id, mode, scenarioMode, isInsightsMode]);

  useEffect(() => {
    const collapsedNodes = initCollapsedNodes(canvasNodes);
    const selectedId = useStore.getState().nodes.find((n) => n.selected)?.id;
    const nodesToApply = selectedId
      ? collapsedNodes.map((node) =>
          node.type === NodesTypes.CustomNode
            ? { ...node, selected: node.id === selectedId }
            : node,
        )
      : collapsedNodes;
    setNodes(nodesToApply);
    setEdges(canvasEdges);
    if (isNewAssetMode) {
      addAsset({ canvasNodes });
    }
    setTimeout(async () => {
      if (mode) {
        CanvasModelRenderer.redrawFullCanvasLayers(mode);
      }
      await collapseRelationController.renderPhantoms();
    }, 0);
  }, [canvasNodes, canvasEdges, isVersionsShown, mode, scenarioMode, id]);

  const onToggleListMenu = () => {
    toggleListMenu();
    resetViewPort(isListMenuOpen ? SIDE_MENU_WIDTH : -SIDE_MENU_WIDTH);
  };

  const isLeftMenuVisible = !isVersionsShown || mode === Modes.RiskManagement;

  return (
    <Stack sx={{ height: 'calc(100vh - 180px)' }}>
      <Stack direction="row" flex={1} position="relative" overflow="hidden">
        {isLeftMenuVisible && <ListMenu open={isListMenuOpen} />}

        <Stack id="board-wrapper" flex={1} position="relative">
          <Box flex={1}>{isVersionsShown ? <Stage /> : <MainStage />}</Box>

          {!isVersionsShown && <CanvasEntityEditSaveHint />}

          {isLeftMenuVisible && (
            <ControlsBar
              isListMenuOpen={isListMenuOpen}
              onToggleListMenu={onToggleListMenu}
            />
          )}
        </Stack>

        {isVersionsShown && <Versions />}

        <DrawersOutlet />
      </Stack>
    </Stack>
  );
});
